import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ErrorType } from '@ndvr/auth-api';

import { CustomerApi } from 'API';
import { Toast } from 'widgets';

export const useHouseholdFilesList = (globalAccountGUID?: string) => {
    return useQuery({
        queryKey: ['globalAccount', globalAccountGUID, 'files'],
        queryFn: async () => {
            if (globalAccountGUID) {
                const [response] = await CustomerApi().listAdvisorFilesForGlobalAccount(globalAccountGUID);
                return response ?? undefined;
            }
        },
    });
};

export const useHouseholdClientFilesList = (globalAccountGUID?: string) => {
    return useQuery({
        queryKey: ['globalAccount', globalAccountGUID, 'client-files'],
        queryFn: async () => {
            if (globalAccountGUID) {
                const [response] = await CustomerApi().listClientFilesForGlobalAccount(globalAccountGUID);
                return response ?? undefined;
            }
        },
    });
};

export const useUploadHouseholdFile = (globalAccountGUID: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (file: File) => {
            return CustomerApi({ hideError: true }).uploadFileForGlobalAccount(globalAccountGUID, {
                fileToUpload: file as any,
            });
        },
        onSuccess: () => {
            Toast.success('File uploaded');
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', globalAccountGUID, 'files'],
            });
        },
        onError: (e: any) => {
            const message =
                e.errorType === ErrorType.EX_CONFLICT
                    ? 'A file with the same name already exists'
                    : 'Failed to upload file';
            Toast.forError(e, message);
        },
    });
};

export const useDownloadHouseholdFileUrl = (globalAccountGUID: string) =>
    useMutation({
        mutationFn: async (file: string) => {
            const [response] = await CustomerApi().getDownloadUrlForGlobalAccountFile(globalAccountGUID, file);
            return response ?? undefined;
        },
    });

export const useDeleteHouseholdFile = (globalAccountGUID: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (fileId: string) => {
            return CustomerApi({ hideError: true }).deleteFileForGlobalAccount(globalAccountGUID, fileId);
        },
        onSuccess: () => {
            Toast.success('File deleted');
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', globalAccountGUID, 'files'],
            });
        },
        onError: (e: any) => {
            Toast.forError(e, 'Failed to delete file');
        },
    });
};
